import WorkingFileUploader from '@/components/AdvancedUpload';
import BatchList from '@/components/BatchList';
import FileList from '@/components/FileList';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useSubscription } from '@/hooks/useSubscription';
import { useUpload } from '@/hooks/useUpload';
import PageSkeleton from '@/layout/Skeleton';
import { AlertTriangle, HardDrive } from 'lucide-react';
import { FunctionComponent, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useOutletContext } from 'react-router';

interface iindexProps {}

const formatBytes = (bytes: number, k: number = 1024): string => {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const index: FunctionComponent<iindexProps> = () => {
  const { isUploading, cancelUpload, startUploads, files, addFiles, removeFile, clearFiles, storageInfo } = useUpload();
  const { organization } = useOrganizations();
  const { initialLoadComplete } = useSubscription();
  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  // Calculate storage status
  const storageStatus = useMemo(() => {
    const storageUsedBytes = organization?.storageUsedBytes ?? 0;
    const storageLimitBytes = storageInfo.storageLimitBytes;
    const pendingBytes = storageInfo.pendingUploadBytes;
    const totalAfterUpload = storageUsedBytes + pendingBytes;
    const wouldExceedLimit = totalAfterUpload > storageLimitBytes;
    const usagePercent = Math.min((storageUsedBytes / storageLimitBytes) * 100, 100);
    const projectedPercent = Math.min((totalAfterUpload / storageLimitBytes) * 100, 100);
    const remainingBytes = Math.max(storageLimitBytes - storageUsedBytes, 0);

    return {
      storageUsedBytes,
      storageLimitBytes,
      pendingBytes,
      totalAfterUpload,
      wouldExceedLimit,
      usagePercent,
      projectedPercent,
      remainingBytes,
    };
  }, [organization?.storageUsedBytes, storageInfo.storageLimitBytes, storageInfo.pendingUploadBytes]);

  return (
    <PageSkeleton
      steps={
        projectLoading
          ? [{ label: 'Loading...', href }]
          : [
              { label: project.name, href },
              {
                label: 'Data Center',
                href: `${href}/upload`,
              },
            ]
      }>
      <div className='max-w-screen-md px-4 mx-auto w-full pb-12'>
        <h1 className='text-xl font-semibold mt-12'>Upload Files</h1>
        <p className='text-sm text-gray-500 mb-4'>
          Upload files to your project. You can upload multiple files at once.
        </p>

        {/* Storage Usage Bar */}
        <div className='mb-6 p-4 bg-slate-50 dark:bg-stone-900 rounded-lg'>
          <div className='flex items-center gap-2 mb-2'>
            <HardDrive className='h-4 w-4 text-slate-500' />
            <span className='text-sm font-medium text-slate-700 dark:text-stone-300'>Storage</span>
          </div>
          {!initialLoadComplete ? (
            <div className='space-y-2'>
              <Skeleton className='h-2 w-full' />
              <div className='flex justify-between'>
                <Skeleton className='h-3 w-24' />
                <Skeleton className='h-3 w-20' />
              </div>
            </div>
          ) : (
            <>
              <Progress value={storageStatus.projectedPercent} className='h-2 mb-2' />
              <div className='flex justify-between text-xs text-slate-500 dark:text-stone-400'>
                <span>
                  {formatBytes(storageStatus.storageUsedBytes, 1000)} used
                  {storageStatus.pendingBytes > 0 && (
                    <span className='text-blue-500'> + {formatBytes(storageStatus.pendingBytes, 1000)} pending</span>
                  )}
                </span>
                <span>{formatBytes(storageStatus.storageLimitBytes, 1000)} limit</span>
              </div>
              {storageStatus.remainingBytes > 0 && (
                <p className='text-xs text-slate-400 dark:text-stone-500 mt-1'>
                  {formatBytes(storageStatus.remainingBytes, 1000)} remaining
                </p>
              )}
            </>
          )}
        </div>

        {/* Storage Limit Warning */}
        {initialLoadComplete && storageStatus.wouldExceedLimit && files.length > 0 && (
          <Alert variant='destructive' className='mb-6'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>Storage limit exceeded</AlertTitle>
            <AlertDescription>
              These files ({formatBytes(storageStatus.pendingBytes)}) would exceed your storage limit. You have{' '}
              {formatBytes(storageStatus.remainingBytes)} remaining. Please remove some files or upgrade your storage
              plan.
            </AlertDescription>
          </Alert>
        )}

        {/* Near Limit Warning */}
        {initialLoadComplete && !storageStatus.wouldExceedLimit && storageStatus.usagePercent >= 90 && (
          <Alert className='mb-6'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>Storage almost full</AlertTitle>
            <AlertDescription>
              You're using {storageStatus.usagePercent.toFixed(0)}% of your storage. Consider upgrading your plan to
              avoid running out of space.
            </AlertDescription>
          </Alert>
        )}

        <WorkingFileUploader onFilesAdded={addFiles} />
        {files.length > 0 && (
          <>
            <Label className='mb-2'>Uploaded Items ({files.length} files):</Label>
            <FileList files={files} onRemoveFile={removeFile} onClearAll={clearFiles} />
          </>
        )}
        <div className='mt-8 mb-8'>
          {!isUploading && files.length > 0 && (
            <Button onClick={startUploads} disabled={!initialLoadComplete || storageStatus.wouldExceedLimit}>
              {!initialLoadComplete
                ? 'Loading...'
                : storageStatus.wouldExceedLimit
                  ? 'Storage limit exceeded'
                  : 'Start Upload'}
            </Button>
          )}
          {isUploading && (
            <Button variant='destructive' onClick={cancelUpload}>
              Cancel Upload
            </Button>
          )}
        </div>
        <ErrorBoundary fallback={<div>Error</div>}>
          <BatchList />
        </ErrorBoundary>
      </div>
    </PageSkeleton>
  );
};

export default index;
